import { useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Dropdown, Popover } from 'antd';
import { BarChartOutlined } from '@ant-design/icons';
import { LuChevronDown, LuChevronRight, LuLogOut, LuUser } from 'react-icons/lu';
import { useLogout, useUser } from '../../../config/connection/auth';
import { LOGIN_ROUTE } from '../../../config';
import { SidebarContext } from '../../../providers/SidebarProvider';
import { useReportContext } from '../../../providers/ReportContext';
import { AuthUser } from '../../../features/auth';
import { Avatar } from '../../Elements/Avatar';
import { itemDaRota } from './SidebarData';
import { ProgressoNoCabecalho } from './ProgressoNoCabecalho';
import { SinoDeAvisos } from '../../../features/avisos/components/AvisosDeMudanca';

/** Paginas que ficam "dentro" de um item da barra e ganham um degrau no caminho. */
const SUBPAGINAS: Record<string, string> = {
    report: 'Relatório de Estoque Ideal',
    'report-rem': 'Relatório de Remarcação',
};

function Header() {
    const { isOpen } = useContext(SidebarContext);
    const { isGeneratingReport, resumoHeader, acoesHeader } = useReportContext();
    const navigate = useNavigate();
    const location = useLocation();
    const logout = useLogout();
    const auth = useUser();

    const usuario = auth.data as AuthUser | undefined;
    const nome = usuario?.name || 'Usuário';

    const exit = async () => {
        await logout.mutateAsync({});
        navigate(LOGIN_ROUTE);
    }

    const item = itemDaRota(location.pathname);
    const subpagina = SUBPAGINAS[location.pathname.replace(/^\/app\//, '').split('/')[0]];

    return (
        <header className={`header${isOpen ? '' : ' header--recolhido'}`}>
            <div className={resumoHeader || acoesHeader ? 'header-left header-left--resumo' : 'header-left'}>
                {/*
                  * Com o resumo rolado para fora da tela, os totais ocupam o
                  * lugar do caminho da pagina: e a informacao que o usuario precisa
                  * ter a mao enquanto percorre a tabela.
                  */}
                {acoesHeader
                    ? <div className="header-acoes">{acoesHeader}</div>
                    : resumoHeader
                    ? (
                        <div className="header-resumo">
                            {/* A remarcacao nao tem graficos de participacao; sem isto o
                              * botao subiria para abrir um balao vazio. */}
                            {resumoHeader.graficos && (
                                <Popover
                                    content={resumoHeader.graficos}
                                    trigger="click"
                                    placement="bottomLeft"
                                    overlayClassName="resumo-graficos-popover"
                                >
                                    <button type="button" className="resumo-graficos-trigger hoverable">
                                        <BarChartOutlined />
                                        Gráficos
                                    </button>
                                </Popover>
                            )}
                            {resumoHeader.totais.map(item => (
                                <div className="header-resumo-item" key={item.label}>
                                    <span className="header-resumo-label">{item.label}</span>
                                    {/* O original vai no canto superior direito do valor, como nas celulas. */}
                                    <span className="header-resumo-valor">
                                        <span>{item.valor}</span>
                                        {item.original && <small className="resumo-original" title={`Projeção original: ${item.original}`}>{item.original}</small>}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )
                    : (
                        <div className="header-pagina">
                            {item && <span className="header-pagina-icone" aria-hidden="true">{item.icon}</span>}
                            <div className="header-pagina-textos">
                                {/* Acima do titulo, de onde a pagina vem: o item da barra, ou a marca. */}
                                <nav className="header-caminho" aria-label="Você está em">
                                    {subpagina && item ? <>
                                        <Link to={`/app/${item.link}`} className="header-caminho-link">{item.title}</Link>
                                        <LuChevronRight className="header-caminho-seta" aria-hidden="true" />
                                    </> : (
                                        <span>Realiza</span>
                                    )}
                                </nav>
                                <span className="header-pagina-titulo" aria-current="page">
                                    {subpagina ?? item?.title ?? 'Realiza'}
                                </span>
                            </div>
                        </div>
                    )}
            </div>

            <div className="header-direita">
                {/* Leva ao dashboard, que e onde o progresso do relatorio aparece. */}
                {isGeneratingReport && <>
                    <ProgressoNoCabecalho />
                    <span className="header-divisor" aria-hidden="true" />
                </>}

                {/* Central de notificações: mudanças em compras/lotes já enviados. */}
                <SinoDeAvisos />

                <Dropdown
                    trigger={['click']}
                    placement="bottomRight"
                    menu={{
                        items: [
                            {
                                key: 'conta',
                                type: 'group',
                                label: (
                                    <div className="header-conta">
                                        <strong>{nome}</strong>
                                        {usuario?.email && <span>{usuario.email}</span>}
                                    </div>
                                ),
                            },
                            { type: 'divider' },
                            { key: 'perfil', label: 'Meu perfil', icon: <LuUser /> },
                            { key: 'sair', label: 'Sair', icon: <LuLogOut />, danger: true },
                        ],
                        onClick: ({ key }) => key === 'sair' ? exit() : navigate('/app/profile'),
                    }}
                >
                    <button type="button" className="header-usuario" aria-label="Menu da conta">
                        <Avatar name={nome} size={30}className="header-usuario-avatar" />
                        <span className="header-usuario-textos">
                            <span className="header-usuario-nome">{nome}</span>
                            {usuario?.email && <span className="header-usuario-email">{usuario.email}</span>}
                        </span>
                        <LuChevronDown className="header-usuario-seta" />
                    </button>
                </Dropdown>
            </div>
        </header>
    )
}

export default Header;
